import httpRequest from "../../ajax";

import {
  GET_FEED_CARDS,
  FEED_CARDS_LOADING,
  FEED_CARDS_ERROR,
  ERROR,
} from "../actionTypes";

/**
 * Fetch activity cards for the Workbrook tab.
 *
 * Calls GET /feed-cards?page=N on the API. In Phase 1 the backend
 * returns stubbed sample cards. In Phase 3 it will return real
 * system-generated cards from the feed_cards table.
 *
 * @param {Function} dispatch
 * @param {number} page — pagination page (default 1)
 * @returns {Promise<{success: boolean, payload: Array}>}
 */
export const getFeedCardsAction = (dispatch, page = 1) => {
  dispatch({ type: FEED_CARDS_LOADING, payload: true });

  return httpRequest("get", `/feed-cards?page=${page}`)
    .then((response) => {
      const responseData = response.data?.data || response.data;
      const pagination = response.data?.pagination || null;

      if (responseData) {
        const action = {
          type: GET_FEED_CARDS,
          payload: {
            cards: responseData,
            page,
            hasMore: pagination ? pagination.has_more : false,
          },
        };
        dispatch(action);
        return {
          success: true,
          payload: responseData,
          hasMore: pagination ? pagination.has_more : false,
        };
      }
      throw new Error("Invalid response format");
    })
    .catch((error) => {
      const errorMessage =
        error.response?.data?.message || error.message || "Failed to fetch feed cards";
      dispatch({ type: FEED_CARDS_ERROR, payload: errorMessage });
      dispatch({ type: ERROR, payload: errorMessage });
      throw new Error(errorMessage);
    })
    .finally(() => {
      dispatch({ type: FEED_CARDS_LOADING, payload: false });
    });
};

/**
 * Check if the current user can create editorial content.
 * Calls GET /feed-cards/editorial-permission on the API.
 *
 * @returns {Promise<{success: boolean, can_create: boolean, is_admin: boolean}>}
 */
export const checkEditorialPermissionAction = () => {
  return httpRequest("get", `/feed-cards/editorial-permission`)
    .then((response) => {
      const data = response.data?.data || response.data;
      return {
        success: true,
        can_create: data?.can_create || false,
        is_admin: data?.is_admin || false,
      };
    })
    .catch((error) => {
      return {
        success: false,
        can_create: false,
        is_admin: false,
      };
    });
};

/**
 * Publish an editorial article from the web app.
 * Calls POST /feed-cards/editorial on the API.
 *
 * @param {Object} payload — { title, article_body, category, preview_text?, canonical_url?, broadcast_flag?, image_url? }
 * @returns {Promise<{success: boolean}>}
 */
export const publishEditorialAction = (payload) => {
  return httpRequest("post", `/feed-cards/editorial`, payload)
    .then((response) => {
      return {
        success: true,
        data: response.data?.data || response.data,
        message: response.data?.message || "Article published successfully",
      };
    })
    .catch((error) => {
      const message =
        error.response?.data?.message || error.message || "Failed to publish article";
      return { success: false, message };
    });
};

/**
 * Get the current broadcast usage/limit status.
 * Calls GET /feed-cards/broadcast-status on the API.
 *
 * @returns {Promise<{success: boolean, monthly_limit: number, sent_this_month: number, remaining: number, limit_reached: boolean}>}
 */
export const getBroadcastStatusAction = () => {
  return httpRequest("get", `/feed-cards/broadcast-status`)
    .then((response) => {
      const data = response.data?.data || response.data;
      return {
        success: true,
        monthly_limit: data?.monthly_limit ?? 1,
        sent_this_month: data?.sent_this_month ?? 0,
        remaining: data?.remaining ?? 0,
        limit_reached: data?.limit_reached ?? false,
      };
    })
    .catch((error) => {
      return {
        success: false,
        monthly_limit: 1,
        sent_this_month: 0,
        remaining: 0,
        limit_reached: false,
      };
    });
};

/**
 * Upload an image for the editorial article content (Quill editor).
 * Calls POST /feed-cards/editorial/upload-image on the API.
 *
 * Note: This uses the admin app's upload endpoint. For the web app,
 * we use Cloudinary directly (same as community posts).
 *
 * @param {File} file
 * @returns {Promise<{success: boolean, url: string}>}
 */
export const uploadEditorialImageAction = (file) => {
  const formData = new FormData();
  formData.append("image", file);

  return httpRequest("post", `/feed-cards/editorial/upload-image`, formData)
    .then((response) => {
      const url = response.data?.url || response.data?.data?.url;
      return { success: !!url, url };
    })
    .catch((error) => {
      return { success: false, url: null };
    });
};
